import { useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckIcon from "@mui/icons-material/Check";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import { useAuth0 } from "@auth0/auth0-react";

import CustomAvatar from "components/common/Avatar";
import AppMenu, { MenuItem as AppMenuItem } from "components/common/Menu";
import Notifications from "components/Widgets/NotificationsV2";
import { baseUrl } from "util/APIBaseUrl";
import { getUserAdditionalInfo } from "selectors/user";
import { LogoutUser } from "store/actions/Logout";
import {
  CUSTOMER_PROFILE_ROUTE,
  EMPLOYEE_PROFILE_ROUTE,
  SETTINGS_ROUTE,
  REFER_A_BUSINESS_ROUTE,
} from "constants/routes";
import { usePermissions } from "components/Hook";

import { STEP_LABELS } from "./types";
import "./Topbar.scss";

// ─── Purple Edge Logo (from Figma) ───────────────────────────────────────────

export function PurpleEdgeLogo() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#rah-logo-clip)">
        <path
          d="M20.4435 16.8253C20.184 16.5604 19.8814 16.3522 19.5447 16.2067C19.1962 16.0563 18.8261 15.9796 18.4442 15.9796C18.0622 15.9796 17.6921 16.0558 17.3436 16.2067C17.007 16.3522 16.7048 16.5604 16.4449 16.8253C16.4439 16.8263 16.4372 16.8331 16.4372 16.8331C15.9554 17.323 15.3248 17.5684 14.6942 17.5684C14.0636 17.5684 13.4331 17.3235 12.9513 16.8331L12.9435 16.8253C12.6841 16.5604 12.3814 16.3522 12.0448 16.2067C11.6963 16.0563 11.3262 15.9796 10.9442 15.9796C10.5623 15.9796 10.1922 16.0558 9.8437 16.2067C9.50705 16.3522 9.20489 16.5604 8.94494 16.8253C8.6855 17.0902 8.48166 17.3992 8.33907 17.7429C8.19185 18.0987 8.1167 18.4766 8.1167 18.8666C8.1167 19.2566 8.19134 19.6345 8.33907 19.9903C8.48166 20.334 8.6855 20.6425 8.94494 20.9079C9.20437 21.1728 9.50705 21.3809 9.8437 21.5265C10.1922 21.6768 10.5623 21.7536 10.9442 21.7536C11.3262 21.7536 11.6963 21.6774 12.0448 21.5265C12.3804 21.3815 12.682 21.1744 12.941 20.9105C12.9446 20.9069 12.9477 20.9037 12.9513 20.9C13.4331 20.4102 14.0636 20.1648 14.6942 20.1648C15.3248 20.1648 15.9554 20.4097 16.4372 20.9L16.448 20.9111L16.4454 20.9079C16.7048 21.1728 17.0075 21.3809 17.3442 21.5265C17.6926 21.6768 18.0628 21.7536 18.4447 21.7536C18.8266 21.7536 19.1968 21.6774 19.5452 21.5265C19.8819 21.3809 20.184 21.1728 20.444 20.9079C20.7034 20.643 20.9073 20.334 21.0499 19.9903C21.1971 19.6345 21.2722 19.2566 21.2722 18.8666C21.2722 18.4766 21.1976 18.0987 21.0499 17.7429C20.9073 17.3992 20.7034 17.0907 20.444 16.8253H20.4435Z"
          fill="#914DE8"
        />
        <path
          d="M20.4415 1.5116C20.1821 1.24671 19.8794 1.03859 19.5427 0.893003C19.1943 0.742689 18.8241 0.665955 18.4422 0.665955C18.0603 0.665955 17.6901 0.742163 17.3417 0.893003C17.005 1.03859 16.7029 1.24671 16.4429 1.5116L16.4388 1.51581C15.9565 2.00827 15.3243 2.25424 14.6922 2.25424C14.0601 2.25424 13.428 2.00827 12.9457 1.51581L12.9343 1.50424L12.9416 1.5116C12.6821 1.24671 12.3794 1.03859 12.0428 0.893003C11.6943 0.742689 11.3242 0.665955 10.9423 0.665955C10.5603 0.665955 10.1902 0.742163 9.84172 0.893003C9.50507 1.03859 9.20291 1.24671 8.94296 1.5116L8.94965 1.50424L8.93832 1.51581C8.456 2.00827 7.82388 2.25424 7.19177 2.25424C6.55965 2.25424 5.92753 2.00827 5.44521 1.51581L5.44109 1.5116C5.18166 1.24671 4.87898 1.03859 4.54234 0.893003C4.19385 0.742689 3.82374 0.665955 3.44179 0.665955C3.05985 0.665955 2.68974 0.742163 2.34125 0.893003C2.00461 1.03859 1.70244 1.24671 1.44249 1.5116C1.18306 1.77649 0.979217 2.08553 0.836631 2.42925C0.689412 2.78507 0.614258 3.16295 0.614258 3.55293C0.614258 3.9429 0.688897 4.32079 0.836631 4.6766C0.979217 5.02033 1.18306 5.32884 1.44249 5.59426C1.70193 5.85914 2.00461 6.06727 2.34125 6.21286C2.68974 6.36317 3.05985 6.4399 3.44179 6.4399C3.82374 6.4399 4.19385 6.3637 4.54234 6.21286C4.87898 6.06727 5.18114 5.85914 5.44109 5.59426C5.44161 5.59373 5.44882 5.58637 5.44882 5.58637C5.93062 5.09654 6.5612 4.8511 7.19177 4.8511C7.82234 4.8511 8.45291 5.09601 8.93472 5.58637L8.94965 5.60161L8.94296 5.59426C9.20239 5.85914 9.50507 6.06727 9.84172 6.21286C10.1902 6.36317 10.5603 6.4399 10.9423 6.4399C11.3242 6.4399 11.6943 6.3637 12.0428 6.21286C12.3794 6.06727 12.6816 5.85914 12.9416 5.59426L12.9343 5.60161L12.9493 5.58637C13.4311 5.09654 14.0617 4.8511 14.6922 4.8511C15.3228 4.8511 15.9534 5.09601 16.4352 5.58637C16.4352 5.58637 16.4424 5.59373 16.4429 5.59426C16.7023 5.85914 17.005 6.06727 17.3417 6.21286C17.6901 6.36317 18.0603 6.4399 18.4422 6.4399C18.8241 6.4399 19.1943 6.3637 19.5427 6.21286C19.8794 6.06727 20.1816 5.85914 20.4415 5.59426C20.7009 5.32937 20.9048 5.02033 21.0474 4.6766C21.1946 4.32079 21.2697 3.9429 21.2697 3.55293C21.2697 3.16295 21.1951 2.78507 21.0474 2.42925C20.9048 2.08553 20.7009 1.77702 20.4415 1.5116Z"
          fill="#914DE8"
        />
        <path
          d="M8.94588 13.2508C9.20532 13.5157 9.50748 13.7238 9.84413 13.8689C10.1926 14.0192 10.5627 14.0959 10.9447 14.0959C11.3266 14.0959 11.6967 14.0197 12.0452 13.8689C12.3819 13.7233 12.684 13.5152 12.944 13.2503C13.2034 12.9854 13.4072 12.6763 13.5498 12.3326C13.6971 11.9768 13.7722 11.5989 13.7722 11.2089C13.7722 10.819 13.6976 10.4411 13.5498 10.0853C13.4072 9.74153 13.2034 9.43302 12.944 9.16761C12.6845 8.90272 12.3819 8.69459 12.0452 8.54901C11.6967 8.39869 11.3266 8.32196 10.9447 8.32196C10.5627 8.32196 10.1926 8.39817 9.84413 8.54901C9.50748 8.69459 9.20532 8.90272 8.94537 9.16761L8.95103 9.16183L8.93816 9.17497C8.45636 9.66533 7.82527 9.91077 7.1947 9.91077C6.56412 9.91077 5.93304 9.66533 5.45123 9.17497L5.43579 9.1592L5.44402 9.16761C5.18459 8.90272 4.88191 8.69459 4.54526 8.54901C4.19678 8.39869 3.82667 8.32196 3.44472 8.32196C3.06278 8.32196 2.69267 8.39817 2.34418 8.54901C2.00753 8.69459 1.70537 8.90272 1.44542 9.16761C1.18599 9.4325 0.982147 9.74153 0.839561 10.0853C0.692341 10.4411 0.617188 10.819 0.617188 11.2089C0.617188 11.5989 0.691827 11.9768 0.839561 12.3326C0.982147 12.6763 1.18599 12.9848 1.44542 13.2503C1.70486 13.5152 2.00753 13.7233 2.34418 13.8689C2.69267 14.0192 3.06278 14.0959 3.44472 14.0959C3.82667 14.0959 4.19678 14.0197 4.54526 13.8689C4.8814 13.7238 5.18304 13.5162 5.44248 13.2518L5.44557 13.2487C5.90884 12.7741 6.53839 12.5071 7.1947 12.5071C7.85101 12.5071 8.48055 12.7741 8.94383 13.2487L8.94588 13.2508Z"
          fill="#914DE8"
        />
        <path
          d="M5.44402 16.8253C5.18459 16.5604 4.88191 16.3522 4.54526 16.2067C4.19678 16.0563 3.82667 15.9796 3.44472 15.9796C3.06278 15.9796 2.69267 16.0558 2.34418 16.2067C2.00753 16.3522 1.70537 16.5604 1.44542 16.8253C1.18599 17.0902 0.982147 17.3992 0.839561 17.7429C0.692341 18.0987 0.617188 18.4766 0.617188 18.8666C0.617188 19.2566 0.691827 19.6345 0.839561 19.9903C0.982147 20.334 1.18599 20.6425 1.44542 20.9079C1.70486 21.1728 2.00753 21.3809 2.34418 21.5265C2.69267 21.6768 3.06278 21.7536 3.44472 21.7536C3.82667 21.7536 4.19678 21.6774 4.54526 21.5265C4.88191 21.3809 5.18407 21.1728 5.44402 20.9079C5.70346 20.643 5.9073 20.334 6.04989 19.9903C6.19711 19.6345 6.27226 19.2566 6.27226 18.8666C6.27226 18.4766 6.19762 18.0987 6.04989 17.7429C5.9073 17.3992 5.70346 17.0907 5.44402 16.8253Z"
          fill="#914DE8"
        />
      </g>
      <defs>
        <clipPath id="rah-logo-clip">
          <rect width="22" height="22" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

// ─── Topbar ──────────────────────────────────────────────────────────────────

function Topbar({
  phase,
  currentStep,
  onBack,
  submitted = false,
}: {
  phase: "intro" | "form";
  currentStep: number;
  onBack: () => void;
  submitted?: boolean;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { logout: auth0Logout, isAuthenticated } = useAuth0();
  const { loggedInUser, loggedInUserType } = usePermissions();
  const userAdditionalInfo = useSelector(getUserAdditionalInfo);
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const { userId, name, email, type, superSendSubscriberId, permissions } = (loggedInUser as any) || {};
  const { pictureVersion } = userAdditionalInfo || {};

  const userProfileUrl = useMemo(() => {
    if (!userId) return undefined;
    if (loggedInUserType === "customer") return `${baseUrl}/customerUser/${userId}/logo?id=${pictureVersion}`;
    if (loggedInUserType === "edge") return `${baseUrl}/user/${userId}/logo?id=${pictureVersion}`;
    return `${baseUrl}/employee/${userId}/logo?id=${pictureVersion}`;
  }, [userId, loggedInUserType, pictureVersion]);

  const logoutUser = () => {
    dispatch(LogoutUser() as any);
    if (isAuthenticated) {
      auth0Logout({ returnTo: window.location.origin } as any);
    }
  };

  const navigateToProfile = () => {
    navigate(type === "employee" ? EMPLOYEE_PROFILE_ROUTE : CUSTOMER_PROFILE_ROUTE);
    setMenuOpen(false);
  };

  return (
    <div className="rah-topbar">
      {/* Left: Go back */}
      <button type="button" className="rah-topbar__back" onClick={onBack}>
        <ChevronLeftIcon sx={{ fontSize: 18 }} />
        Go back
      </button>

      {/* Center: Step indicators (only visible in form phase, hidden on success) */}
      <div className="rah-topbar__center">
        {phase === "form" && !submitted && (
          <div className="rah-topbar__steps">
            {STEP_LABELS.map((label, index) => {
              const isCompleted = index < currentStep;
              const isActive = index === currentStep;
              let itemClass = "rah-topbar__step-item";
              let dotClass = "rah-topbar__step-dot";
              if (isCompleted) {
                itemClass += " rah-topbar__step-item--completed";
                dotClass += " rah-topbar__step-dot--completed";
              } else if (isActive) {
                itemClass += " rah-topbar__step-item--active";
                dotClass += " rah-topbar__step-dot--active";
              }
              return (
                <div key={label} className={itemClass}>
                  <div className={dotClass}>
                    {isCompleted ? <CheckIcon sx={{ fontSize: 14, color: "#ffffff" }} /> : <span>{index + 1}</span>}
                  </div>
                  <span className="rah-topbar__step-label">{label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Right: same structure as AppHeaderNew (navbar-actions) */}
      <div className="navbar-actions flex items-center">
        <Notifications userId={userId} subscriberId={superSendSubscriberId} />

        <div className="user-menu-container" ref={anchorRef}>
          <div
            className={`user-menu-trigger flex items-center cursor-pointer py-05 ${menuOpen ? "menu-open" : ""}`}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <div className="user-avatar">
              <CustomAvatar src={userProfileUrl} size="md" />
            </div>
            <ArrowDropDownIcon className="dropdown-icon" />
          </div>

          {menuOpen && (
            <AppMenu
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              anchorEl={anchorRef.current}
              minWidth="280px"
              autoWidth
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <div className="flex items-center p-075 gap-1">
                <CustomAvatar src={userProfileUrl} size="lg" />
                <div className="user-info">
                  <p className="heading-h5-regular">{name || ""}</p>
                  <p className="user-email heading-h5-regular">{email || ""}</p>
                </div>
              </div>

              <div className="divider-bottom" />

              {loggedInUserType === "customer" && (
                <>
                  {/* @ts-ignore — JS component */}
                  <AppMenuItem
                    value="Refer Edge, earn $2,000"
                    leftSlot={<CardGiftcardIcon className="referral-icon" />}
                    className="referral-menu-item"
                    onClick={() => {
                      navigate(REFER_A_BUSINESS_ROUTE);
                      setMenuOpen(false);
                    }}
                  />
                  <div className="divider-bottom" />
                </>
              )}

              {/* @ts-ignore */}
              <AppMenuItem value="Profile" onClick={navigateToProfile} />
              {permissions?.includes("CustomerAdmin") && (
                // @ts-ignore
                <AppMenuItem
                  value="Account Settings"
                  onClick={() => {
                    navigate(SETTINGS_ROUTE);
                    setMenuOpen(false);
                  }}
                />
              )}
              <div className="divider-bottom" />
              {/* @ts-ignore */}
              <AppMenuItem
                value="Logout"
                onClick={() => {
                  logoutUser();
                  setMenuOpen(false);
                }}
              />
            </AppMenu>
          )}
        </div>
      </div>
    </div>
  );
}

export default Topbar;
